import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonGroupModule, TooltipModule } from '@libs/ui-lib';
import { TranslateModule } from '@ngx-translate/core';
import { HealthHistoryDialogService } from '@libs/home';
import { PrescriptionsTabComponent } from '../prescriptions-tab/prescriptions-tab.component';
import { HealthHistoryTabComponent } from '../health-history-tab/health-history-tab.component';
import { PrescriptionsTabService } from '../prescriptions-tab/prescriptions-tab.service';
import { PatientAlertsTabComponent } from '../patient-alerts-tab/patient-alerts-tab.component';

@Component({
  selector: 'lib-health-history-list',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonGroupModule,
    TooltipModule,
    PrescriptionsTabComponent,
    HealthHistoryTabComponent,
    PatientAlertsTabComponent,
  ],
  templateUrl: './health-history-list.component.html',
  styleUrl: './health-history-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HealthHistoryListComponent implements OnInit {
  private readonly healthHistoryDialogService = inject(HealthHistoryDialogService);
  private readonly prescriptionsTabService = inject(PrescriptionsTabService);

  @Input() public initialActiveTabIndex: number = 0;

  protected readonly activeTabIndex = signal(0);

  public ngOnInit(): void {
    this.activeTabIndex.set(this.initialActiveTabIndex);
  }

  protected readonly isPatientHealthHistoryTabActive = computed(() => this.activeTabIndex() === 0);
  protected readonly isPrescriptionsTabActive = computed(() => this.activeTabIndex() === 1);
  protected readonly isPatientAlertsTabActive = computed(() => this.activeTabIndex() === 2);

  protected readonly healthHistoryCount = this.healthHistoryDialogService.healthHistoryCount;
  protected readonly prescriptionsCount = this.prescriptionsTabService.selectPrescriptionsCount;
  protected readonly patientAlertsCount = this.healthHistoryDialogService.patientAlertsCount;
  protected readonly hasDirtyForm = computed(() => this.healthHistoryDialogService.viewModel().hasDirtyForm);

  protected readonly tabs = computed(() => [
    { label: 'health_history_health_history_tab_title_with_count', active: true, count: this.healthHistoryCount() ?? 0 },
    { label: 'health_history_prescriptions_tab_title_with_count', active: false, count: this.prescriptionsCount() ?? 0 },
    { label: 'health_history_patient_alerts_tab_title_with_count', active: false, count: this.patientAlertsCount() ?? 0 },
  ]);

  public setActiveTabIndex(index: number): void {
    if (this.hasDirtyForm()) {
      const dialogRef = this.healthHistoryDialogService.openCancelConfirmationDialog();
      dialogRef.closed.subscribe((result) => {
        if (!result?.confirm) {
          return;
        }
        this.activeTabIndex.set(index);
      });
    } else {
      this.activeTabIndex.set(index);
    }
  }
}
